import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MoneyInput } from '@/components/ui/money-input';
import { RadioGroup, RadioGroupOption } from '@/components/ui/radio-group';
import { SwitchField } from '@/components/ui/switch';
import { usePreferences } from '@/stores/preferences-store';
import { formatMoney } from '@/utils/format';

export const DEFAULT_SUGGESTED_AMOUNTS_MINOR = [2000, 5000, 10000, 20000, 50000];

export type GivingLinkAmountsMode = 'default' | 'custom';

export interface GivingLinkAmountsValue {
  mode: GivingLinkAmountsMode;
  customAmountsMinor: (number | null)[];
  allowCustomAmount: boolean;
}

export const createInitialAmountsValue = (): GivingLinkAmountsValue => ({
  mode: 'default',
  customAmountsMinor: [null, null, null],
  allowCustomAmount: true,
});

export const amountsMatchDefault = (amounts: number[]): boolean =>
  amounts.length === DEFAULT_SUGGESTED_AMOUNTS_MINOR.length &&
  amounts.every((amount, index) => amount === DEFAULT_SUGGESTED_AMOUNTS_MINOR[index]);

export const amountsValueFromLink = (link: {
  suggestedAmountsMinor: number[];
  allowCustomAmount: boolean;
}): GivingLinkAmountsValue => ({
  mode: amountsMatchDefault(link.suggestedAmountsMinor) ? 'default' : 'custom',
  customAmountsMinor: amountsMatchDefault(link.suggestedAmountsMinor)
    ? [null, null, null]
    : link.suggestedAmountsMinor.map((amount) => amount),
  allowCustomAmount: link.allowCustomAmount,
});

export const resolvedSuggestedAmountsMinor = (value: GivingLinkAmountsValue): number[] =>
  value.mode === 'default'
    ? DEFAULT_SUGGESTED_AMOUNTS_MINOR
    : value.customAmountsMinor.filter((amount): amount is number => amount !== null && amount > 0);

export const buildCreateAmountsPayload = (value: GivingLinkAmountsValue) => ({
  ...(value.mode === 'custom'
    ? { suggestedAmountsMinor: resolvedSuggestedAmountsMinor(value) }
    : {}),
  allowCustomAmount: value.allowCustomAmount,
});

export const buildUpdateAmountsPayload = (value: GivingLinkAmountsValue) => ({
  suggestedAmountsMinor: resolvedSuggestedAmountsMinor(value),
  allowCustomAmount: value.allowCustomAmount,
});

export const amountsValueEqualsStored = (
  value: GivingLinkAmountsValue,
  stored: { suggestedAmountsMinor: number[]; allowCustomAmount: boolean },
): boolean => {
  if (value.allowCustomAmount !== stored.allowCustomAmount) return false;
  const resolved = resolvedSuggestedAmountsMinor(value);
  if (resolved.length !== stored.suggestedAmountsMinor.length) return false;
  return resolved.every((amount, index) => amount === stored.suggestedAmountsMinor[index]);
};

export const isAmountsValueValid = (value: GivingLinkAmountsValue): boolean => {
  if (value.mode === 'default') return true;

  const amounts = resolvedSuggestedAmountsMinor(value);
  if (amounts.length < 2 || amounts.length > 12) return false;
  return new Set(amounts).size === amounts.length;
};

export const GivingLinkAmountsField = ({
  idPrefix,
  value,
  onChange,
  currency,
}: {
  idPrefix: string;
  value: GivingLinkAmountsValue;
  onChange: (value: GivingLinkAmountsValue) => void;
  currency?: string;
}) => {
  const { t } = useTranslation('payments');
  const baseCurrency = usePreferences((state) => state.baseCurrency);
  const activeCurrency = currency ?? baseCurrency;

  const defaultAmountsLabel = useMemo(
    () =>
      DEFAULT_SUGGESTED_AMOUNTS_MINOR.map((amount) =>
        formatMoney(amount, activeCurrency),
      ).join(', '),
    [activeCurrency],
  );

  const customAmounts = resolvedSuggestedAmountsMinor(value);
  const hasDuplicateCustomAmounts =
    value.mode === 'custom' &&
    customAmounts.length > 0 &&
    new Set(customAmounts).size !== customAmounts.length;
  const hasTooFewCustomAmounts =
    value.mode === 'custom' && customAmounts.length > 0 && customAmounts.length < 2;

  const setMode = (mode: GivingLinkAmountsMode) => {
    onChange({
      ...value,
      mode,
      customAmountsMinor:
        mode === 'custom' && value.customAmountsMinor.every((amount) => amount === null)
          ? [null, null, null]
          : value.customAmountsMinor,
    });
  };

  const updateCustomAmount = (index: number, amountMinor: number | null) => {
    const next = [...value.customAmountsMinor];
    next[index] = amountMinor;
    onChange({ ...value, customAmountsMinor: next });
  };

  const addCustomAmount = () => {
    if (value.customAmountsMinor.length >= 12) return;
    onChange({ ...value, customAmountsMinor: [...value.customAmountsMinor, null] });
  };

  const removeCustomAmount = (index: number) => {
    if (value.customAmountsMinor.length <= 1) return;
    onChange({
      ...value,
      customAmountsMinor: value.customAmountsMinor.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>{t('links.form.amounts.label')}</Label>
        <RadioGroup
          value={value.mode}
          onValueChange={(next) => setMode(next as GivingLinkAmountsMode)}
          className="mt-2"
        >
          <RadioGroupOption
            id={`${idPrefix}-amounts-default`}
            value="default"
            label={t('links.form.amounts.modeDefault')}
            description={t('links.form.amounts.modeDefaultDescription', {
              amounts: defaultAmountsLabel,
            })}
          />
          <RadioGroupOption
            id={`${idPrefix}-amounts-custom`}
            value="custom"
            label={t('links.form.amounts.modeCustom')}
            description={t('links.form.amounts.modeCustomDescription')}
          />
        </RadioGroup>
      </div>

      {value.mode === 'custom' ? (
        <div className="space-y-2">
          {value.customAmountsMinor.map((amountMinor, index) => (
            <div key={`${idPrefix}-amount-${index}`} className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <Label htmlFor={`${idPrefix}-amount-${index}`}>
                  {t('links.form.amounts.amountLabel', { index: index + 1 })}
                </Label>
                <MoneyInput
                  id={`${idPrefix}-amount-${index}`}
                  value={amountMinor}
                  onChange={(next) => updateCustomAmount(index, next)}
                  currency={activeCurrency}
                  popoverPortal="dialog"
                  invalid={
                    hasDuplicateCustomAmounts &&
                    amountMinor !== null &&
                    customAmounts.filter((amount) => amount === amountMinor).length > 1
                  }
                />
              </div>
              {value.customAmountsMinor.length > 1 ? (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={t('links.form.amounts.removeAmount')}
                  onClick={() => removeCustomAmount(index)}
                >
                  <Trash2 className="size-4" />
                </Button>
              ) : (
                <span className="size-10 shrink-0" aria-hidden />
              )}
            </div>
          ))}

          {value.customAmountsMinor.length < 12 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              leadingIcon={<Plus className="size-4" />}
              onClick={addCustomAmount}
            >
              {t('links.form.amounts.addAmount')}
            </Button>
          ) : null}

          {hasTooFewCustomAmounts ? (
            <p className="text-caption text-danger">{t('links.form.amounts.customMin')}</p>
          ) : null}
          {hasDuplicateCustomAmounts ? (
            <p className="text-caption text-danger">{t('links.form.amounts.customDuplicate')}</p>
          ) : null}
        </div>
      ) : null}

      <SwitchField
        id={`${idPrefix}-allow-custom-amount`}
        label={t('links.form.amounts.allowCustom')}
        description={t('links.form.amounts.allowCustomDescription')}
        checked={value.allowCustomAmount}
        onCheckedChange={(allowCustomAmount) => onChange({ ...value, allowCustomAmount })}
      />
    </div>
  );
};

export const formatSuggestedAmounts = (amountsMinor: number[], currency: string): string =>
  amountsMinor.map((amount) => formatMoney(amount, currency)).join(', ');
